import React, { MouseEventHandler } from 'react';
import { ImageOptionalLink } from '../../../commonTypes/links';
import colors from '../../../helpers/colors';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import Collapsible from '../../Collapsible';
import Icon from '../../Icon';
import LanguageToggle, { LanguageToggleProps } from '../DoubleHeader/LanguageToggle';
import { GridAlignedText } from '../commonStyles/commonHeaderStyles';
import { combineTertiaryNavItems } from '../helpers';
import {
  isPrimaryLinkNavItem,
  isSimplePrimaryNavItem,
  isTertiaryNavItemWithIcon,
  PrimaryNav,
  PrimaryNavProps,
  SecondaryNav,
  SecondaryNavProps,
  TertiaryNavItem,
  TertiaryNavItemWithIcon,
} from '../types';
import { useActiveItems } from '../useActiveItems';
import BurgerMenuIcon from './BurgerMenuIcon';
import NestedPrimaryNavSubmenu from './Menus/NestedPrimaryNavSubmenu';
import SecondaryNavSubmenu from './Menus/SecondaryNavSubmenu';
import SimplePrimaryNavSubmenu from './Menus/SimplePrimaryNavSubmenu';
import {
  IconWithMargin,
  MenuButton as MainMenuButton,
  MenuButtonsContainer,
  MenuContainer,
  PrimaryNavLinkLight,
  StyledPhoneIconLink,
  TertiaryNavIconBackground,
  TertiaryNavItemDark,
  TertiaryNavItemHeading,
} from './styles';

const getPrimaryMenuId = (baseId: string, index: number): string =>
  `${baseId}-primary-menu-${index}`;

const getSecondaryMenuId = (baseId: string, index: number): string =>
  `${baseId}-secondary-menu-${index}`;

const getTertiaryMenuId = (
  baseId: string,
  index: number,
  primaryNav?: PrimaryNav
): string => `${baseId}-tertiary-menu-${(primaryNav?.navItems.length ?? 0) + 1 + index}`;

export const getAllMenuIds = (
  baseId: string,
  primaryNav?: PrimaryNav,
  secondaryNav?: SecondaryNav
): string[] => {
  const primaryNavItems = primaryNav?.navItems ?? [];
  const secondaryNavItems = secondaryNav?.navItems ?? [];

  return [
    ...primaryNavItems.map((_, i) => getPrimaryMenuId(baseId, i)),
    ...secondaryNavItems.map((_, i) => getSecondaryMenuId(baseId, i)),
  ];
};

type NavMobileMenuProps = PrimaryNavProps &
  SecondaryNavProps & {
    menuOpen: boolean;
    baseId: string;
    /** @deprecated use tertiaryNavItems instead - if both are used, this will be ignored */
    tertiaryNavItem?: TertiaryNavItem | TertiaryNavItemWithIcon;
    /** @deprecated use tertiaryNavItems instead - if both are used, this will be ignored */
    secondTertiaryNavItem?: TertiaryNavItem;
    tertiaryNavItems?: (TertiaryNavItem | TertiaryNavItemWithIcon)[];
    languageToggle?: LanguageToggleProps;
    showTertiaryIcons?: boolean;
  } & ComponentProps;

export const NavMobileMenu: React.FC<NavMobileMenuProps> = ({
  primaryNav,
  secondaryNav,
  tertiaryNavItem,
  secondTertiaryNavItem,
  tertiaryNavItems,
  languageToggle,
  showTertiaryIcons = false,
  menuOpen,
  activeItemIndex: parentActiveItemIndex,
  setActiveItemIndex: parentSetActiveItemIndex,
  baseId,
  resetNav,
  ...props
}) => {
  const combinedTertiaryNavItems = combineTertiaryNavItems(
    tertiaryNavItem,
    secondTertiaryNavItem,
    tertiaryNavItems
  );

  // See comment on PrimaryNavDesktop
  const [
    { activeItemIndex, activeNestedItemIndex },
    onClickNavItem,
    onClickNestedNavItem,
  ] = useActiveItems(parentActiveItemIndex, parentSetActiveItemIndex);

  const onClickNavItemWithScroll = (index: number) => () => {
    if (activeItemIndex !== null && activeItemIndex < index) {
      // Opening a new section will close the previous one. If the previous section is
      // above the newly opened section, the new section may be shifted up out of view.
      // We fix this by scrolling to the section being closed. This will only work
      // satisfactorily if the number of primary menu items is few enough, which should
      // be the case
      document
        .getElementById(getPrimaryMenuId(baseId, activeItemIndex))
        ?.scrollIntoView({ behavior: 'smooth' });
    }
    onClickNavItem(index)();
  };

  const secondaryItemIndex = (i: number): number =>
    (primaryNav?.navItems.length ?? 0) + i;

  return (
    <MenuContainer {...componentProps(props)}>
      <Collapsible isOpened={menuOpen}>
        {primaryNav &&
          primaryNav.navItems.map((navItem, i) =>
            // eslint-disable-next-line no-nested-ternary
            isSimplePrimaryNavItem(navItem) ? (
              <SimplePrimaryNavSubmenu
                baseId={getPrimaryMenuId(baseId, i)}
                key={getPrimaryMenuId(baseId, i)}
                navItem={navItem}
                isActive={activeItemIndex === i}
                toggleActive={onClickNavItemWithScroll(i)}
                resetNav={resetNav}
              />
            ) : isPrimaryLinkNavItem(navItem) ? (
              <PrimaryNavLinkLight
                id={`${baseId}-tertiary-menu-${i}`}
                href={navItem.url}
                onClick={navItem.onClick}
                openInNewTab={navItem.openInNewTab}
                // eslint-disable-next-line react/no-array-index-key
                key={`${baseId}-tertiary-menu-${i}`}>
                <GridAlignedText>{navItem.name}</GridAlignedText>
              </PrimaryNavLinkLight>
            ) : (
              <NestedPrimaryNavSubmenu
                baseId={getPrimaryMenuId(baseId, i)}
                key={getPrimaryMenuId(baseId, i)}
                navItem={navItem}
                isActive={activeItemIndex === i}
                toggleActive={onClickNavItemWithScroll(i)}
                activeNestedItemIndex={activeNestedItemIndex}
                setActiveNestedItemIndex={onClickNestedNavItem}
                resetNav={resetNav}
              />
            )
          )}
        {secondaryNav &&
          secondaryNav.navItems.map((navItem, i) => (
            <SecondaryNavSubmenu
              baseId={getSecondaryMenuId(baseId, i)}
              key={getSecondaryMenuId(baseId, i)}
              navItem={navItem}
              isActive={activeItemIndex === secondaryItemIndex(i)}
              toggleActive={onClickNavItemWithScroll(secondaryItemIndex(i))}
              resetNav={resetNav}
            />
          ))}
        {combinedTertiaryNavItems.map((navItem, index) =>
          isTertiaryNavItemWithIcon(navItem) ? (
            <TertiaryNavItemDark
              id={getTertiaryMenuId(baseId, index, primaryNav)}
              key={getTertiaryMenuId(baseId, index, primaryNav)}
              href={navItem.url}
              onClick={navItem.onClick}
              openInNewTab={navItem.openInNewTab}
              aria-label={navItem.screenReaderText}>
              <GridAlignedText>
                {showTertiaryIcons && (
                  <TertiaryNavIconBackground>
                    <Icon name={navItem.icon.name} size={navItem.icon.size ?? 'small'} />
                  </TertiaryNavIconBackground>
                )}
                {navItem.name}
              </GridAlignedText>
            </TertiaryNavItemDark>
          ) : (
            <TertiaryNavItemHeading
              id={getTertiaryMenuId(baseId, index, primaryNav)}
              key={getTertiaryMenuId(baseId, index, primaryNav)}
              href={navItem.url}
              onClick={navItem.onClick}
              openInNewTab={navItem.openInNewTab}
              aria-label={navItem.screenReaderText}>
              <GridAlignedText>{navItem.name}</GridAlignedText>
            </TertiaryNavItemHeading>
          )
        )}
        {languageToggle && <LanguageToggle {...languageToggle} />}
      </Collapsible>
    </MenuContainer>
  );
};

export type NavMobileProps = PrimaryNavProps &
  SecondaryNavProps & {
    menuOpen: boolean;
    toggleMenuOpen: () => void;
    onOpen?: MouseEventHandler;
    onClose?: MouseEventHandler;
    tertiaryNavItem?: TertiaryNavItem | TertiaryNavItemWithIcon;
    secondTertiaryNavItem?: TertiaryNavItem;
    tertiaryNavItems?: (TertiaryNavItem | TertiaryNavItemWithIcon)[];
    languageToggle?: LanguageToggleProps;
    headerText?: string;
    forceHamburgerMenuIcon?: boolean;
    showTertiaryIcons?: boolean;
    headerCloseText?: string;
    baseIdPrefix?: string;
    phoneIconLink?: ImageOptionalLink;
  } & ComponentProps;

const NavMobile: React.FC<NavMobileProps> = ({
  primaryNav,
  secondaryNav,
  menuOpen,
  toggleMenuOpen,
  onOpen,
  onClose,
  tertiaryNavItem,
  secondTertiaryNavItem,
  tertiaryNavItems,
  languageToggle,
  activeItemIndex,
  setActiveItemIndex,
  resetNav,
  headerText,
  forceHamburgerMenuIcon = false,
  showTertiaryIcons = false,
  headerCloseText,
  baseIdPrefix = '',
  phoneIconLink,
  ...props
}) => {
  if (!primaryNav && !secondaryNav) {
    return null;
  }

  const onMenuButtonClick: MouseEventHandler = (event) => {
    if (onOpen && !menuOpen) {
      onOpen(event);
    }
    if (onClose && menuOpen) {
      onClose(event);
    }
    toggleMenuOpen();
    setActiveItemIndex(null); // Close all submenus
  };

  const getMenuIcon = (): JSX.Element => {
    if (
      tertiaryNavItem &&
      isTertiaryNavItemWithIcon(tertiaryNavItem) &&
      !forceHamburgerMenuIcon
    ) {
      return (
        <IconWithMargin
          name={tertiaryNavItem.icon.name}
          size={tertiaryNavItem.icon.size ?? 'small'}
          color={colors.core01}
          displayVariant={!menuOpen}
        />
      );
    }
    return <BurgerMenuIcon menuOpen={menuOpen} color={colors.core03} />;
  };

  const getMenuText = (): JSX.Element => {
    let text = headerText ? <b>{headerText}</b> : 'Menu';
    if (menuOpen) {
      text = headerCloseText ? <b>{headerCloseText}</b> : 'Menu';
    }
    return <span>{text}</span>;
  };

  return (
    <div {...componentProps(props)}>
      <MenuButtonsContainer>
        {phoneIconLink && (
          <StyledPhoneIconLink logoLink={phoneIconLink} shouldEagerLoad />
        )}
        <MainMenuButton
          onClick={onMenuButtonClick}
          aria-expanded={menuOpen}
          aria-controls={getAllMenuIds(
            `${baseIdPrefix}header`,
            primaryNav,
            secondaryNav
          ).join(' ')}>
          {!phoneIconLink && getMenuText()}
          {getMenuIcon()}
        </MainMenuButton>
      </MenuButtonsContainer>
      <NavMobileMenu
        data-testid="mobile-menu"
        baseId={`${baseIdPrefix}header`}
        primaryNav={primaryNav}
        secondaryNav={secondaryNav}
        tertiaryNavItem={tertiaryNavItem}
        secondTertiaryNavItem={secondTertiaryNavItem}
        tertiaryNavItems={tertiaryNavItems}
        showTertiaryIcons={showTertiaryIcons}
        languageToggle={languageToggle}
        activeItemIndex={activeItemIndex}
        setActiveItemIndex={setActiveItemIndex}
        menuOpen={menuOpen}
        resetNav={resetNav}
      />
    </div>
  );
};

export default applyComponentTheme(NavMobile, 'NavMobile');
